import { ask } from '@monorepo/tools/src/lib/tools/ask/ask';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { IRequestOptions } from '@monorepo/tools/src/lib/interfaces/url';
import { URLSearchParams } from 'url';
import { WebappsCidModel, IWebappsCid } from '../models/webapps-cid.model';
import { ISegmentation } from '../models/campaign-segmentation.model';
import { ICidReplaceForm } from '../models/cid.model';
import endpoints from './endpoints/index.endpoints';
import { webappsCidRequestQueue } from '../utils/webapps-cid-request-queue';

const toWebappsCidList = (res: unknown): IWebappsCid[] => (Array.isArray(res) ? res : ((res as { cids?: IWebappsCid[] })?.cids ?? []));

export interface IWebappsCidSegmentsCreateForm {
	cid_segments: {
		auction_package_id: id;
		auction_package_name: string;
		target_cid: id;
	}[];
}

const toAffectedCampaignIds = (res: unknown): id[] => {
	const list = Array.isArray(res) ? res : ((res as { campaigns?: unknown[] })?.campaigns ?? []);

	return list
		.map(item => {
			if (typeof item === 'number' || typeof item === 'string') {
				return item as id;
			}

			if (item && typeof item === 'object') {
				const record = item as Record<string, unknown>;
				return (record.id ?? record.campaign_id) as id | undefined;
			}

			return undefined;
		})
		.filter((campaignId): campaignId is id => campaignId != null && campaignId !== '');
};

export const formatAffectedCampaignsText = (campaignIds: id[]): string => campaignIds.map(String).join('\n');

export const WebappsCidApi = {
	list: async (params?: URLSearchParams): Promise<WebappsCidModel[]> => {
		return webappsCidRequestQueue.enqueue('list', async () => {
			const res = await ask.get(endpoints.webappsCid.list(), params);
			return toWebappsCidList(res) as WebappsCidModel[];
		});
	},

	linkedCids: async (params?: URLSearchParams): Promise<WebappsCidModel[]> => {
		return webappsCidRequestQueue.enqueue('linkedCids', () => ask.get(endpoints.webappsCid.linkedCids(), params));
	},

	getCids: async (cid: id, params?: URLSearchParams): Promise<WebappsCidModel[]> => {
		return webappsCidRequestQueue.enqueue(`getCids:${cid}`, () => ask.get(endpoints.webappsCid.getCids(cid), params));
	},

	getSegments: async (cid: id, params?: URLSearchParams): Promise<{ cid_segments: ISegmentation[] }> => {
		return ask.get(endpoints.webappsCid.getSegments(cid), params);
	},

	getCampaigns: async (cid: id): Promise<id[]> => {
		const res = await ask.get(endpoints.webappsCid.getCampaigns(cid));
		return toAffectedCampaignIds(res);
	},

	createSegments: async (cid: id, body: IWebappsCidSegmentsCreateForm, options?: IRequestOptions): Promise<void> => {
		return ask.post(endpoints.webappsCid.createSegments(cid), body, options);
	},

	replaceCid: async (cid: id, body: ICidReplaceForm, options?: IRequestOptions): Promise<void> => {
		return ask.post(endpoints.webappsCid.replaceCid(cid), body, options);
	},
};
